import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { FileSpreadsheet, FileText, RefreshCw, User as UserIcon, Calendar } from "lucide-react";
import { api, formatSAR } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Daily shift report for the currently-logged-in cashier.
 *
 * - Flattens each transaction into one row per service item so we can show
 *   the therapist commission breakdown (5% Pertama · Bonus 5% · Bonus 10%)
 *   in the same format as the "Rekap Terapis" detail modal.
 * - Excel + PDF export so the cashier can hand in an end-of-shift report.
 */
export default function KasirDailyReport() {
  const { user } = useAuth();
  const isPrivileged = user?.role === "admin" || user?.role === "owner";

  // Default date: today (Riyadh local YYYY-MM-DD)
  const todayRiyadh = useMemo(() => {
    const now = new Date();
    // Convert to Asia/Riyadh via toLocaleString and parse pieces
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Riyadh", year: "numeric", month: "2-digit", day: "2-digit",
    }).formatToParts(now);
    const y = parts.find((p) => p.type === "year").value;
    const m = parts.find((p) => p.type === "month").value;
    const d = parts.find((p) => p.type === "day").value;
    return `${y}-${m}-${d}`;
  }, []);

  const [date, setDate] = useState(todayRiyadh);
  // For admin/owner: "" = ALL cashiers (default). For kasir: locked to their own id.
  const [selectedCashier, setSelectedCashier] = useState(isPrivileged ? "" : (user?.id || ""));
  const [cashiers, setCashiers] = useState([]);
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load cashier list (for admin/owner dropdown). Kasir can skip this call.
  useEffect(() => {
    if (!isPrivileged) return;
    api.get("/reports/cashiers-list")
      .then((r) => setCashiers(r.data || []))
      .catch(() => { /* non-fatal; dropdown just stays empty */ });
  }, [isPrivileged]);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const params = {
        date_from: date,
        date_to: date,
        include_cancelled: false,
        page: 1,
        page_size: 500,
      };
      // Kasir role is ALWAYS locked to their own id (privacy);
      // admin/owner can optionally filter by a specific cashier.
      if (!isPrivileged) {
        params.cashier_id = user.id;
      } else if (selectedCashier) {
        params.cashier_id = selectedCashier;
      }
      const { data } = await api.get("/transactions", { params });
      setTxs(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat laporan");
    } finally {
      setLoading(false);
    }
  }, [user?.id, date, isPrivileged, selectedCashier]);

  useEffect(() => { load(); }, [load]);

  // Flatten items into rows — one line per service handled
  const rows = useMemo(() => {
    const out = [];
    for (const t of txs) {
      const items = t.items || [];
      for (const it of items) {
        const price = it.price || 0;
        const stored_up = it.commission_upsell || 0;
        let b5 = !!it.bonus_5;
        let b10 = !!it.bonus_10;
        // Legacy inference (mirrors backend _build_earning_rows)
        if (!("bonus_5" in it) && !("bonus_10" in it) && stored_up > 0 && price > 0) {
          const r = stored_up / price;
          b5 = (r > 0.04 && r < 0.06) || (r > 0.14 && r < 0.16);
          b10 = (r > 0.09 && r < 0.11) || (r > 0.14 && r < 0.16);
        }
        const bonus_5 = b5 ? +(price * 0.05).toFixed(2) : 0;
        const bonus_10 = b10 ? +(price * 0.10).toFixed(2) : 0;
        out.push({
          tx_id: t.id,
          created_at: t.created_at,
          service: it.service_name_id,
          therapist: it.therapist_name,
          price,
          customer_phone: t.customer_phone,
          commission_base: it.commission_base || 0,
          commission_bonus_5: bonus_5,
          commission_bonus_10: bonus_10,
          total_commission: +((it.commission_base || 0) + bonus_5 + bonus_10).toFixed(2),
          payment_method: t.payment_method,
          cashier_id: t.cashier_id,
          cashier_name: t.cashier_name,
        });
      }
    }
    return out;
  }, [txs]);

  const totals = useMemo(() => {
    const sesi = rows.length;
    const base = rows.reduce((s, r) => s + r.commission_base, 0);
    const b5 = rows.reduce((s, r) => s + r.commission_bonus_5, 0);
    const b10 = rows.reduce((s, r) => s + r.commission_bonus_10, 0);
    const rev = txs.reduce((s, t) => s + (t.total || 0), 0);
    return {
      sesi,
      base: +base.toFixed(2),
      bonus_5: +b5.toFixed(2),
      bonus_10: +b10.toFixed(2),
      commission_total: +(base + b5 + b10).toFixed(2),
      revenue: +rev.toFixed(2),
      tx_count: txs.length,
    };
  }, [rows, txs]);

  const fmtDT = (iso) => {
    try {
      return new Date(iso).toLocaleString("id-ID", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", timeZone: "Asia/Riyadh",
      });
    } catch { return iso; }
  };

  // Kasir column only visible for admin/owner when viewing all cashiers.
  const showKasirColumn = isPrivileged && !selectedCashier;
  const colSpan = showKasirColumn ? 10 : 9;

  function exportExcel() {
    if (rows.length === 0) { toast.error("Tidak ada data untuk diekspor"); return; }
    const stamp = new Date().toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Riyadh" });
    const scopeLabel = showKasirColumn
      ? "Semua Kasir"
      : (isPrivileged && selectedCashier
          ? (cashiers.find((c) => c.id === selectedCashier)?.name || "—")
          : (user?.name || "—"));
    const meta = [
      ["JAVA SPA — LAPORAN HARIAN KASIR"],
      [],
      ["Kasir:", scopeLabel],
      ["Telepon:", isPrivileged && !selectedCashier ? "—" : (user?.phone || "—")],
      ["Tanggal Laporan:", date],
      ["Dicetak:", stamp],
      [],
      ["RINGKASAN"],
      ["Jumlah Transaksi", totals.tx_count],
      ["Jumlah Sesi (Item)", totals.sesi],
      ["Total Pendapatan (SAR)", totals.revenue],
      ["Komisi 5% Pertama (SAR)", totals.base],
      ["Bonus 5% (SAR)", totals.bonus_5],
      ["Bonus 10% (SAR)", totals.bonus_10],
      ["Total Komisi Terapis (SAR)", totals.commission_total],
      [],
      ["DETAIL TRANSAKSI"],
      showKasirColumn
        ? ["Tanggal", "Kasir", "Layanan", "Terapis", "Harga (SAR)", "Pelanggan", "5% Pertama", "Bonus 5%", "Bonus 10%", "Total Komisi", "Metode Bayar"]
        : ["Tanggal", "Layanan", "Terapis", "Harga (SAR)", "Pelanggan", "5% Pertama", "Bonus 5%", "Bonus 10%", "Total Komisi", "Metode Bayar"],
    ];
    const body = rows.map((r) => showKasirColumn
      ? [fmtDT(r.created_at), r.cashier_name || "—", r.service, r.therapist, r.price, r.customer_phone, r.commission_base, r.commission_bonus_5, r.commission_bonus_10, r.total_commission, r.payment_method || "cash"]
      : [fmtDT(r.created_at), r.service, r.therapist, r.price, r.customer_phone, r.commission_base, r.commission_bonus_5, r.commission_bonus_10, r.total_commission, r.payment_method || "cash"]
    );
    const ws = XLSX.utils.aoa_to_sheet([...meta, ...body]);
    ws["!cols"] = showKasirColumn
      ? [{ wch: 22 }, { wch: 18 }, { wch: 22 }, { wch: 18 }, { wch: 12 }, { wch: 18 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 12 }]
      : [{ wch: 22 }, { wch: 22 }, { wch: 18 }, { wch: 12 }, { wch: 18 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Laporan Kasir");
    const safeName = scopeLabel.replace(/[^a-z0-9]/gi, "-").toLowerCase();
    XLSX.writeFile(wb, `Java-SPA_Laporan-Kasir_${safeName}_${date}.xlsx`);
    toast.success("Laporan Excel berhasil diunduh");
  }

  function exportPDF() {
    if (rows.length === 0) { toast.error("Tidak ada data untuk diekspor"); return; }
    const stamp = new Date().toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Asia/Riyadh" });
    const scopeLabel = showKasirColumn
      ? "Semua Kasir"
      : (isPrivileged && selectedCashier
          ? (cashiers.find((c) => c.id === selectedCashier)?.name || "—")
          : (user?.name || "—"));
    const phoneLine = isPrivileged && !selectedCashier ? "-" : (user?.phone || "-");
    const html = `<!doctype html><html lang="id"><head><meta charset="utf-8"><title>Laporan Kasir — ${escapeHtml(scopeLabel)} — ${date}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Roboto, sans-serif; color: #1c1c1c; padding: 24px; }
  .brand { border-bottom: 2px solid #b78638; padding-bottom: 12px; margin-bottom: 18px; }
  .brand h1 { margin: 0; font-size: 22px; color: #b78638; letter-spacing: 2px; }
  .brand .sub { color: #6b6b6b; font-size: 11px; margin-top: 4px; }
  .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 4px 12px; font-size: 12px; margin-bottom: 18px; }
  .meta .k { color: #6b6b6b; }
  .stats { display: grid; grid-template-columns: repeat(6, 1fr); gap: 8px; margin-bottom: 16px; }
  .stat { border: 1px solid #ddd; border-radius: 6px; padding: 8px; text-align: center; }
  .stat .k { font-size: 9px; color: #6b6b6b; text-transform: uppercase; letter-spacing: 1px; }
  .stat .v { font-size: 14px; font-weight: 700; margin-top: 2px; }
  .stat.gold { border-color: #b78638; background: #fdf6e6; }
  .stat.gold .v { color: #b78638; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  th { background: #1c1c1c; color: #fff; padding: 6px 4px; text-align: start; font-size: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
  td { padding: 5px 4px; border-bottom: 1px solid #eee; }
  td.num, th.num { text-align: end; font-variant-numeric: tabular-nums; }
  .total-row td { background: #f5f5f5; font-weight: 700; padding-top: 8px; padding-bottom: 8px; }
  .footer { margin-top: 24px; font-size: 10px; color: #6b6b6b; text-align: center; }
  @media print { body { padding: 12px; } }
</style></head><body>
<div class="brand">
  <h1>JAVA SPA · LAPORAN HARIAN KASIR</h1>
  <div class="sub">Riyadh · Luxury Spa · Trilingual POS</div>
</div>
<div class="meta">
  <div><span class="k">Kasir:</span> <b>${escapeHtml(scopeLabel)}</b></div>
  <div><span class="k">Tanggal Laporan:</span> <b>${date}</b></div>
  <div><span class="k">Telepon:</span> ${escapeHtml(phoneLine)}</div>
  <div><span class="k">Dicetak:</span> ${stamp}</div>
</div>
<div class="stats">
  <div class="stat"><div class="k">Transaksi</div><div class="v">${totals.tx_count}</div></div>
  <div class="stat"><div class="k">Sesi</div><div class="v">${totals.sesi}</div></div>
  <div class="stat gold"><div class="k">Pendapatan</div><div class="v">${formatSAR(totals.revenue)}</div></div>
  <div class="stat"><div class="k">5% Pertama</div><div class="v">${formatSAR(totals.base)}</div></div>
  <div class="stat"><div class="k">Bonus 5%</div><div class="v">${formatSAR(totals.bonus_5)}</div></div>
  <div class="stat"><div class="k">Bonus 10%</div><div class="v">${formatSAR(totals.bonus_10)}</div></div>
</div>
<table>
  <thead>
    <tr>
      <th>Tanggal</th>
      ${showKasirColumn ? "<th>Kasir</th>" : ""}
      <th>Layanan</th>
      <th>Terapis</th>
      <th class="num">Harga</th>
      <th>Pelanggan</th>
      <th class="num">5% Pertama</th>
      <th class="num">Bonus 5%</th>
      <th class="num">Bonus 10%</th>
      <th class="num">Total Komisi</th>
    </tr>
  </thead>
  <tbody>
    ${rows.map((r) => `<tr>
      <td>${escapeHtml(fmtDT(r.created_at))}</td>
      ${showKasirColumn ? `<td>${escapeHtml(r.cashier_name || "")}</td>` : ""}
      <td>${escapeHtml(r.service || "")}</td>
      <td>${escapeHtml(r.therapist || "")}</td>
      <td class="num">${formatSAR(r.price)}</td>
      <td>${escapeHtml(r.customer_phone || "")}</td>
      <td class="num">${formatSAR(r.commission_base)}</td>
      <td class="num">${r.commission_bonus_5 > 0 ? formatSAR(r.commission_bonus_5) : "—"}</td>
      <td class="num">${r.commission_bonus_10 > 0 ? formatSAR(r.commission_bonus_10) : "—"}</td>
      <td class="num">${formatSAR(r.total_commission)}</td>
    </tr>`).join("")}
    <tr class="total-row">
      <td colspan="${showKasirColumn ? 6 : 5}" style="text-align:end">TOTAL</td>
      <td class="num">${formatSAR(totals.base)}</td>
      <td class="num">${formatSAR(totals.bonus_5)}</td>
      <td class="num">${formatSAR(totals.bonus_10)}</td>
      <td class="num">${formatSAR(totals.commission_total)}</td>
    </tr>
  </tbody>
</table>
<div class="footer">Java SPA · Riyadh · Dokumen ini dicetak otomatis dari sistem POS pada ${stamp}.</div>
<script>window.onload = () => setTimeout(() => window.print(), 300);</script>
</body></html>`;
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank");
    if (!w) toast.error("Popup diblokir — mohon izinkan popup untuk ekspor PDF");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  return (
    <div className="card-luxury p-5 mt-8" data-testid="kasir-daily-report">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-heading text-xl md:text-2xl text-gold flex items-center gap-2">
            <UserIcon className="h-5 w-5" /> Laporan Harian {isPrivileged && !selectedCashier ? "" : "Saya"}
          </h2>
          <div className="text-xs text-foreground/60 mt-0.5">
            {isPrivileged && !selectedCashier ? (
              <>Kasir: <span className="text-foreground font-medium">Semua Kasir</span></>
            ) : (
              <>
                Kasir:{" "}
                <span className="text-foreground font-medium">
                  {isPrivileged && selectedCashier
                    ? (cashiers.find((c) => c.id === selectedCashier)?.name || "—")
                    : user?.name}
                </span>
                {!isPrivileged && user?.phone && <span className="text-foreground/40"> · {user.phone}</span>}
              </>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isPrivileged && (
            <label className="inline-flex items-center gap-1.5 text-xs bg-charcoal-900/60 border border-charcoal-600 rounded-md px-2 py-1.5">
              <UserIcon className="h-3.5 w-3.5 text-gold" />
              <select
                value={selectedCashier}
                onChange={(e) => setSelectedCashier(e.target.value)}
                data-testid="kasir-report-filter"
                className="bg-transparent outline-none text-sm text-foreground min-w-[140px]"
              >
                <option value="">Semua Kasir</option>
                {cashiers.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </label>
          )}
          <label className="inline-flex items-center gap-1.5 text-xs bg-charcoal-900/60 border border-charcoal-600 rounded-md px-2 py-1.5">
            <Calendar className="h-3.5 w-3.5 text-gold" />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              data-testid="kasir-report-date"
              className="bg-transparent outline-none text-sm text-foreground min-w-[130px]"
            />
          </label>
          <button
            onClick={load}
            data-testid="kasir-report-refresh"
            className="p-2 rounded-md border border-charcoal-600 hover:border-gold text-foreground/70 hover:text-gold transition"
            title="Muat ulang"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={exportExcel}
            data-testid="kasir-report-excel"
            className="btn-leaf text-xs py-2 px-3"
            title="Ekspor Excel"
          >
            <FileSpreadsheet className="h-3.5 w-3.5" /> Excel
          </button>
          <button
            onClick={exportPDF}
            data-testid="kasir-report-pdf"
            className="btn-outline-gold text-xs py-2 px-3"
            title="Ekspor / Cetak PDF"
          >
            <FileText className="h-3.5 w-3.5" /> PDF
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
        <StatCard label="Transaksi" value={totals.tx_count} />
        <StatCard label="Sesi" value={totals.sesi} />
        <StatCard label="Pendapatan" value={formatSAR(totals.revenue)} accent="gold" testId="kasir-report-revenue" />
        <StatCard label="5% Pertama" value={formatSAR(totals.base)} />
        <StatCard label="Bonus 5%" value={formatSAR(totals.bonus_5)} accent="leaf-soft" />
        <StatCard label="Bonus 10%" value={formatSAR(totals.bonus_10)} accent="leaf" />
      </div>

      {/* Table */}
      <div className="overflow-x-auto scrollbar-thin border border-charcoal-600/40 rounded-lg">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-[0.15em] text-foreground/60 bg-charcoal-900/60">
            <tr>
              <th className="text-start px-3 py-2">Tanggal</th>
              {showKasirColumn && <th className="text-start px-3 py-2">Kasir</th>}
              <th className="text-start px-3 py-2">Layanan</th>
              <th className="text-start px-3 py-2">Terapis</th>
              <th className="text-end px-3 py-2">Harga</th>
              <th className="text-start px-3 py-2">Pelanggan</th>
              <th className="text-end px-3 py-2">5% Pertama</th>
              <th className="text-end px-3 py-2">Bonus 5%</th>
              <th className="text-end px-3 py-2">Bonus 10%</th>
              <th className="text-end px-3 py-2">Total Komisi</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={colSpan} className="text-center py-8 text-foreground/40">Memuat…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={colSpan} className="text-center py-8 text-foreground/40">
                Belum ada transaksi {isPrivileged && !selectedCashier ? "" : "Anda"} pada tanggal {date}.
              </td></tr>
            )}
            {!loading && rows.map((r, i) => (
              <tr key={`${r.tx_id}-${i}`} className="border-t border-charcoal-600/30 hover:bg-gold/5">
                <td className="px-3 py-2 whitespace-nowrap">{fmtDT(r.created_at)}</td>
                {showKasirColumn && <td className="px-3 py-2 text-foreground/70 whitespace-nowrap">{r.cashier_name || "—"}</td>}
                <td className="px-3 py-2">{r.service}</td>
                <td className="px-3 py-2 text-foreground/80">{r.therapist}</td>
                <td className="px-3 py-2 text-end text-gold font-mono">{formatSAR(r.price)}</td>
                <td className="px-3 py-2 text-foreground/60">{r.customer_phone}</td>
                <td className="px-3 py-2 text-end">{formatSAR(r.commission_base)}</td>
                <td className="px-3 py-2 text-end text-leaf/80">
                  {r.commission_bonus_5 > 0 ? formatSAR(r.commission_bonus_5) : <span className="text-foreground/30">—</span>}
                </td>
                <td className="px-3 py-2 text-end text-leaf">
                  {r.commission_bonus_10 > 0 ? formatSAR(r.commission_bonus_10) : <span className="text-foreground/30">—</span>}
                </td>
                <td className="px-3 py-2 text-end text-gold font-medium">{formatSAR(r.total_commission)}</td>
              </tr>
            ))}
            {!loading && rows.length > 0 && (
              <tr className="border-t-2 border-gold/40 bg-gold/5 font-heading">
                <td colSpan={showKasirColumn ? 6 : 5} className="px-3 py-2 text-end uppercase text-[11px] tracking-[0.2em] text-foreground/70">Total</td>
                <td className="px-3 py-2 text-end">{formatSAR(totals.base)}</td>
                <td className="px-3 py-2 text-end text-leaf/80">{formatSAR(totals.bonus_5)}</td>
                <td className="px-3 py-2 text-end text-leaf">{formatSAR(totals.bonus_10)}</td>
                <td className="px-3 py-2 text-end text-gold">{formatSAR(totals.commission_total)}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({ label, value, accent, testId }) {
  const styles = {
    gold: "bg-gold/10 border-gold/30 text-gold",
    leaf: "bg-leaf/10 border-leaf/30 text-leaf",
    "leaf-soft": "bg-leaf/5 border-leaf/20 text-leaf/80",
  };
  const cls = styles[accent] || "bg-charcoal-900/60 border-charcoal-600";
  return (
    <div className={`rounded-lg border p-3 ${cls}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.15em] text-foreground/60">{label}</div>
      <div className="text-lg font-heading mt-1">{value}</div>
    </div>
  );
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ));
}
